/**
 * Event-money helpers: the single source for which currencies events can be
 * priced in and how amounts render.
 *
 * Why an allowlist instead of "any currency": Stripe can CHARGE in ~135
 * currencies, but our UK platform can only PAY hosts via Connect cross-border
 * transfers between the US, Canada, the UK, the EEA and Switzerland
 * (docs.stripe.com/connect/cross-border-payouts). An event priced in a
 * currency we cannot settle to its host would strand the host's earnings, so
 * the list below is exactly the major currencies of payable regions. All are
 * two-decimal currencies, which keeps every cents-based calculation in
 * financial-math valid. The API enforces the same list (single source of
 * truth duplicated knowingly across the two apps — see
 * apps/api/src/services/stripe/currencies.ts; both are covered by tests).
 */

export const SUPPORTED_CURRENCIES = [
  { code: 'USD', label: 'US Dollar ($)' },
  { code: 'GBP', label: 'British Pound (£)' },
  { code: 'EUR', label: 'Euro (€)' },
  { code: 'CAD', label: 'Canadian Dollar (CA$)' },
  { code: 'CHF', label: 'Swiss Franc (CHF)' },
  { code: 'SEK', label: 'Swedish Krona (SEK)' },
  { code: 'NOK', label: 'Norwegian Krone (NOK)' },
  { code: 'DKK', label: 'Danish Krone (DKK)' },
  { code: 'PLN', label: 'Polish Złoty (PLN)' },
  { code: 'CZK', label: 'Czech Koruna (CZK)' },
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number]['code'];

const SUPPORTED_CODES = new Set<string>(SUPPORTED_CURRENCIES.map((entry) => entry.code));

export function isSupportedCurrency(value: unknown): value is SupportedCurrency {
  return typeof value === 'string' && SUPPORTED_CODES.has(value.toUpperCase());
}

export function normalizeCurrency(value: unknown, fallback: SupportedCurrency = 'USD'): SupportedCurrency {
  return isSupportedCurrency(value) ? (value.toUpperCase() as SupportedCurrency) : fallback;
}

/**
 * Format minor units (cents) in an event's currency: $15.00, £15.00, €15.00,
 * CA$15.00, SEK 15.00. Uses a fixed 'en' locale so output is deterministic
 * across browsers and in tests.
 */
export function formatMoney(cents: number, currency: string | null | undefined): string {
  const normalized = normalizeCurrency(currency);
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: normalized,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Format a major-unit amount (e.g. form inputs held in "dollars"). */
export function formatMoneyMajor(amount: number, currency: string | null | undefined): string {
  return formatMoney(Math.round(amount * 100), currency);
}

/**
 * Like formatMoney but drops ".00" on whole amounts: $3, £1,000, €12.50.
 * For compact UI like tip preset buttons and inline price chips.
 */
export function formatMoneyCompact(cents: number, currency: string | null | undefined): string {
  return formatMoney(cents, currency).replace(/\.00(?=\D*$|$)/, '');
}

/** The currency symbol/prefix alone, for input adornments: $, £, €, CA$, SEK. */
export function currencySymbol(currency: string | null | undefined): string {
  const normalized = normalizeCurrency(currency);
  const parts = new Intl.NumberFormat('en', { style: 'currency', currency: normalized }).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? normalized;
}
